<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import conversationAPI from 'dashboard/api/conversations';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ContactDetailsItem from './ContactDetailsItem.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();

const dialogRef = ref(null);
const reason = ref('');
const requestError = ref('');
const isSubmitting = ref(false);

const canSubmit = computed(
  () => reason.value.trim().length > 0 && reason.value.length <= 500
);

watch(reason, () => {
  requestError.value = '';
});

const resetDialog = () => {
  reason.value = '';
  requestError.value = '';
  isSubmitting.value = false;
};

const openDialog = () => {
  resetDialog();
  dialogRef.value?.open();
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const responseMessage = (response, fallback) =>
  response?.data?.message || response?.data?.error || fallback;

const finalizeTicket = async () => {
  if (!canSubmit.value || isSubmitting.value) return;

  isSubmitting.value = true;
  requestError.value = '';

  try {
    const response = await conversationAPI.finalizeAurora(
      props.conversationId,
      {
        motivo_finalizacao: reason.value.trim(),
      }
    );

    useAlert(
      responseMessage(response, t('CONVERSATION.AURORA_FINALIZE.SUCCESS'))
    );
    closeDialog();
  } catch (error) {
    requestError.value = responseMessage(
      error.response,
      t('CONVERSATION.AURORA_FINALIZE.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="mt-4">
    <ContactDetailsItem
      compact
      :title="t('CONVERSATION.AURORA_FINALIZE.SECTION_TITLE')"
    />
    <NextButton
      type="button"
      class="w-full"
      variant="faded"
      color="ruby"
      icon="i-lucide-circle-check"
      :label="t('CONVERSATION.AURORA_FINALIZE.BUTTON')"
      @click="openDialog"
    />

    <Dialog
      ref="dialogRef"
      type="alert"
      width="md"
      :title="t('CONVERSATION.AURORA_FINALIZE.TITLE')"
      :description="t('CONVERSATION.AURORA_FINALIZE.DESCRIPTION')"
      :confirm-button-label="t('CONVERSATION.AURORA_FINALIZE.CONFIRM')"
      :cancel-button-label="t('CONVERSATION.AURORA_FINALIZE.CANCEL')"
      :disable-confirm-button="!canSubmit"
      :is-loading="isSubmitting"
      @confirm="finalizeTicket"
      @close="resetDialog"
    >
      <TextArea
        id="aurora-finalization-reason"
        v-model="reason"
        :label="t('CONVERSATION.AURORA_FINALIZE.REASON_LABEL')"
        :placeholder="t('CONVERSATION.AURORA_FINALIZE.REASON_PLACEHOLDER')"
        :max-length="500"
        show-character-count
        auto-height
        autofocus
        :disabled="isSubmitting"
        :message="requestError"
        :message-type="requestError ? 'error' : 'info'"
      />
    </Dialog>
  </div>
</template>
